import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { Calendar, MapPin, ExternalLink } from "lucide-react";
import Seo, { breadcrumbLd, eventLd } from "../components/Seo";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/events")
      .then(({ data }) => {
        if (alive) setEvents(data);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div data-testid="events-page" className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
      <Seo
        title="Events — Openings, Fairs & Talks on African Art"
        description="Upcoming openings, fairs, residencies and talks shaping African art across the continent and the diaspora. Curated by editors, artists, and writers in the network."
        path="/events"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Events", path: "/events" },
          ]),
          ...events.slice(0, 20).map((e) =>
            eventLd({ event: e, url: `${window.location.origin}/events#${e.id}` }),
          ),
        ]}
      />
      <p className="label-eyebrow text-[#1E3A8A]">— Upcoming</p>
      <h1 className="font-serif-display text-5xl md:text-7xl tracking-tighter leading-none font-medium mt-4 text-[#111]">
        Events
      </h1>
      <p className="text-[#555] mt-4 max-w-2xl">
        Openings, fairs, residencies, and talks shaping African art across the
        continent and the diaspora. Curated by editors, artists, and writers in
        the network.
      </p>

      <div className="mt-16">
        {loading ? (
          <p className="label-eyebrow text-[#555]">— Loading</p>
        ) : events.length === 0 ? (
          <div className="border border-dashed border-[#E5E0D8] p-12 text-center">
            <p className="font-serif-display text-3xl text-[#555]">No events on the calendar yet.</p>
            <p className="text-sm text-[#555] mt-2">
              Writers and approved artists can submit events from their dashboard.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {events.map((e) => (
              <article
                key={e.id}
                data-testid={`event-card-${e.id}`}
                className="border border-[#E5E0D8] bg-white group hover:border-[#1E3A8A] transition-colors"
              >
                {e.cover_image ? (
                  <div className="aspect-[4/3] bg-[#F4EFE6] overflow-hidden">
                    <img
                      src={resolveImg(e.cover_image)}
                      alt={`${e.title} — African contemporary art event`}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-[#F4EFE6] flex items-center justify-center">
                    <Calendar className="w-12 h-12 text-[#1E3A8A]/30" strokeWidth={1} />
                  </div>
                )}
                <div className="p-6">
                  <p className="label-eyebrow text-[#1E3A8A] flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {fmtDate(e.start_at)}
                  </p>
                  <h3 className="font-serif-display text-2xl tracking-tight font-medium mt-3 text-[#111]">
                    {e.title}
                  </h3>
                  {e.location && (
                    <p className="text-sm text-[#555] mt-2 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {e.location}
                    </p>
                  )}
                  <p className="text-sm text-[#333] mt-4 line-clamp-3">{e.description}</p>
                  <div className="mt-5 flex items-center justify-between text-xs">
                    <span className="text-[#555]">By {e.organizer_name}</span>
                    {e.external_url && (
                      <a
                        href={e.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`event-rsvp-${e.id}`}
                        className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1"
                      >
                        RSVP <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
